import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function HeaderMenuConsumer({
  isMobile = false,
  closeMobileMenu
}: {
  isMobile?: boolean;
  closeMobileMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className={cn("flex gap-4", isMobile ? "flex-col items-center" : "flex-row")}>
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-20 bg-muted/50 animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col items-center gap-6">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-xl font-medium text-secondary hover:text-chart-3 transition-colors tracking-[0.1em]" onClick={closeMobileMenu} data-index={i}>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium text-secondary tracking-[0.1em] transition-colors hover:text-secondary/80 bg-transparent hover:bg-transparent focus:bg-transparent focus:text-secondary">
                {item.label}
                <span className="absolute left-1/2 bottom-[-4px] h-[2px] w-0 -translate-x-1/2 bg-[hsl(43_91%_53%)] transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm" : "bg-background")}>
      <div className="container mx-auto h-20 flex items-center justify-between !p-4">
        {}
        <Link to="/HomePage#" className="flex-shrink-0">
          <img src="/img/IMG_1410.png" alt="Banquetes Orzuna" className="object-contain !h-[150px] !w-[100px]" />
        </Link>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="17" className="flex items-center">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <Button variant="ghost" size="icon" className="md:hidden text-secondary hover:bg-secondary/10 hover:text-secondary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-20 left-0 w-full bg-background border-b border-border shadow-lg animate-in slide-in-from-top-5 fade-in duration-200">
          <div className="container mx-auto px-4 py-8 flex flex-col items-center gap-6">
            <MenuProvider menu_id="17">
              <HeaderMenuConsumer isMobile closeMobileMenu={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            {}
          </div>
        </div>}
    </header>;
}